import { z } from 'zod';

export const ThingShadowSchema = z.object({
  state: z.object({
    desired: z.record(z.string(), z.any()).optional(),
    reported: z.record(z.string(), z.any()).optional(),
    delta: z.record(z.string(), z.any()).optional(),
  }),
  version: z.number().optional(),
  clientToken: z.string().max(64).optional(),
});
export type ThingShadow = z.infer<typeof ThingShadowSchema>;

export const FleetShadowSchema = z.object({
  desired: z.record(z.string(), z.any()).optional(),
  reported: z.record(z.string(), z.any()).optional(),
  delta: z.record(z.string(), z.any()).optional(),
  version: z.number().optional(),
  metadata: z.record(z.string(), z.any()).optional(),
});
export type FleetShadow = z.infer<typeof FleetShadowSchema>;

export const idleConfigShadowName: string = 'IdleConfig';
export const IdleConfigShadowDataSchema = z.object({
  url: z.url(),
  // Grrr. We use this schema both for SENDING TO as well as RECEVING data FROM the shadow.
  // But: `null` in SENDING means "delete this property from the shadow", whereas `null` can NEVER be received:
  // https://docs.aws.amazon.com/iot/latest/developerguide/device-shadow-data-flow.html#deleting-thing-data
  // So the actually correct design would be to have TWO separate schemas: one for sending updates to the shadow.
  // But we just use `.partial()` when receiving data to allow for `null` values, which is a bit hacky. 🫣
  config: z.string().nullable().optional(),
});
export type IdleConfigShadowData = z.infer<typeof IdleConfigShadowDataSchema>;

export const sessionShadowName: string = 'Session';
export const SessionShadowDataSchema = z
  .object({
    sessionId: z.string().uuid().nullable(),
    // Unix timestamp in milliseconds; the shadow field name is part of the wire contract.
    expiresAt: z.number().int().min(0).nullable(),
    url: z.string().nullable(),
    config: z.string().nullable(),
  })
  .partial();
export type SessionShadowData = z.infer<typeof SessionShadowDataSchema>;

export const packageStateShadowName: string = '$package';
export const PackageStateShadowDataSchema = z
  .object({
    kiosk: z.object({
      version: z.string(),
    }),
    chromeOs: z.object({
      version: z.string().nullish(),
    }),
  })
  .partial();
export type PackageStateShadowData = z.infer<typeof PackageStateShadowDataSchema>;

export const FleetIndexShadowSchema = z.object({
  name: z.object({
    [sessionShadowName]: FleetShadowSchema.optional(),
    [idleConfigShadowName]: FleetShadowSchema.optional(),
  }),
});

const IdleConfigShadowStateSchema = z.object({
  url: z.url(),
});

function extractIdleConfigUrlFromState(state: unknown): string | undefined {
  const parsed = IdleConfigShadowStateSchema.safeParse(state);
  return parsed.success ? parsed.data.url : undefined;
}

export function extractIdleConfigUrlFromThingShadow(shadow: ThingShadow): string | undefined {
  return extractIdleConfigUrlFromState(shadow.state.reported);
}

export function extractIdleConfigUrlFromFleetShadow(shadow: FleetShadow | undefined): string | undefined {
  return extractIdleConfigUrlFromState(shadow?.reported);
}
